import { TypographyVariant } from '@ben/orion/utils/theme';
import RadioButton from '@ben/orion/radio-button';
import Typography from '@ben/orion/typography';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import {
  WindowSubTypeEnum,
  WindowTypeEnum,
} from '@ben/shared/types/modular-benefit';
import type { Control } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import styled, { css, useTheme } from 'styled-components';

interface IEnrolmentWindowType {
  index: number;
  control: Control<IModularBenefitDetail>;
}

const EnrolmentWindowType = ({ index, control }: IEnrolmentWindowType) => {
  const theme = useTheme();
  const benefitWindows = useWatch<IModularBenefitDetail>({
    name: 'benefitWindows',
    exact: true,
  });

  return (
    <Wrapper>
      <Typography variant={TypographyVariant.bodyLg2}>Window type</Typography>
      <Controller
        name={`benefitWindows.${index}.windowType`}
        control={control}
        render={({ field }) => {
          const { value, ...rest } = field;
          return (
            <RowWrapper>
              <RadioButton
                key={`config-window-${index}`}
                {...rest}
                label="Config window"
                checked={!value || value === WindowTypeEnum.CONFIG_WINDOW}
                backgroundColor={theme.palette.neutral['00']}
                value={WindowTypeEnum.CONFIG_WINDOW}
              />
              <RadioButton
                key={`opt-in-${index}`}
                {...rest}
                label="Opt-in"
                checked={value === WindowTypeEnum.OPT_IN_WINDOW}
                backgroundColor={theme.palette.neutral['00']}
                value={WindowTypeEnum.OPT_IN_WINDOW}
              />
              <RadioButton
                key={`opt-out-${index}`}
                {...rest}
                label="Opt-out"
                checked={value === WindowTypeEnum.OPT_OUT_WINDOW}
                backgroundColor={theme.palette.neutral['00']}
                value={WindowTypeEnum.OPT_OUT_WINDOW}
              />
              <RadioButton
                key={`locked-window-${index}`}
                {...rest}
                label="Locked"
                checked={value === WindowTypeEnum.LOCKED_WINDOW}
                backgroundColor={theme.palette.neutral['00']}
                value={WindowTypeEnum.LOCKED_WINDOW}
              />
            </RowWrapper>
          );
        }}
      />
      {benefitWindows?.[index]?.windowType === WindowTypeEnum.LOCKED_WINDOW && (
        <>
          <Typography variant={TypographyVariant.bodyMd2}>
            Window Sub type
          </Typography>
          <Controller
            name={`benefitWindows.${index}.windowSubType`}
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <RowWrapper>
                  <RadioButton
                    key={`sub-type-none-${index}`}
                    {...rest}
                    label="None"
                    checked={!value || value === WindowSubTypeEnum.NONE}
                    backgroundColor={theme.palette.neutral['00']}
                    value={WindowSubTypeEnum.NONE}
                  />
                  <RadioButton
                    key={`sub-type-minimum-commitment-${index}`}
                    {...rest}
                    label="Minimum commitment term"
                    checked={
                      value === WindowSubTypeEnum.MINIMUM_COMMITMENT_TERM
                    }
                    backgroundColor={theme.palette.neutral['00']}
                    value={WindowSubTypeEnum.MINIMUM_COMMITMENT_TERM}
                  />
                </RowWrapper>
              );
            }}
          />
        </>
      )}
    </Wrapper>
  );
};

export default EnrolmentWindowType;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const RowWrapper = styled.div`
  ${({ theme }) => css`
    gap: ${theme.spacing(3)};
    display: flex;
    flex-direction: row;
  `}
`;
